// ==================================================================================
// Digital Approval System (DAS) - Dashboard Hooks
// ==================================================================================
// Custom hooks for dashboard statistics operations
// ==================================================================================

import { useState, useEffect } from 'react';
import { dashboardService } from '../../../api/services/das/dashboardService';
import {
  DashboardSummaryDto,
  RequestStatusSummaryDto,
  RequestTypeSummaryDto,
  ApprovalMetricsDto,
  MyWorkSummaryDto,
  RecentActivityDto,
} from '../../../api/types/das/dashboard.types';

/**
 * Hook to get dashboard summary
 */
export const useDashboardSummary = () => {
  const [summary, setSummary] = useState<DashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getSummary();
      setSummary(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboard summary';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  return { summary, loading, error, refetch: fetchSummary };
};

/**
 * Hook to get status summary
 */
export const useStatusSummary = () => {
  const [statusSummary, setStatusSummary] = useState<RequestStatusSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatusSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getStatusSummary();
      setStatusSummary(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load status summary';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatusSummary();
  }, []);

  return { statusSummary, loading, error, refetch: fetchStatusSummary };
};

/**
 * Hook to get type summary
 */
export const useTypeSummary = () => {
  const [typeSummary, setTypeSummary] = useState<RequestTypeSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTypeSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getTypeSummary();
      setTypeSummary(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load type summary';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypeSummary();
  }, []);

  return { typeSummary, loading, error, refetch: fetchTypeSummary };
};

/**
 * Hook to get approval metrics
 */
export const useApprovalMetrics = () => {
  const [metrics, setMetrics] = useState<ApprovalMetricsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getApprovalMetrics();
      setMetrics(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load approval metrics';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return { metrics, loading, error, refetch: fetchMetrics };
};

/**
 * Hook to get my work summary
 */
export const useMyWorkSummary = () => {
  const [workSummary, setWorkSummary] = useState<MyWorkSummaryDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getMyWorkSummary();
      setWorkSummary(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load work summary';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkSummary();
  }, []);

  return { workSummary, loading, error, refetch: fetchWorkSummary };
};

/**
 * Hook to get recent activity
 */
export const useRecentActivity = (topN?: number) => {
  const [activity, setActivity] = useState<RecentActivityDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivity = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dashboardService.getRecentActivity(topN);
      setActivity(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load recent activity';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [topN]);

  return { activity, loading, error, refetch: fetchActivity };
};
